import { computed, inject, Injectable, resource } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { lastValueFrom } from 'rxjs';
import { UserData } from '../../../../auth/services/state/user-data';
import { ROUTE_PATHS } from '../../../../routing/routes.pojo';
import { LanguageService } from '../../../../shared/services/language/language.service';
import { ConsentDataClient } from '../../../consent/services/consent-data-client';
import { ConsentState } from '../../../consent/services/consent-state';
import { PrescriptionData } from '../../interfaces/prescriptionData.interface';
import { Question } from '../../interfaces/question.interface';
import { ConsultationDataClient } from '../apis/consultation/consultation-data-client';
import { ConsultationRequest } from '../apis/consultation/interfaces/consultation-payload.interface';
import {
  ConsultationQuestionDto,
  ConsultationStep,
} from '../apis/consultation/interfaces/consultation-step.interface';
import { ConsultationResponse } from '../apis/consultation/interfaces/consultation.interface';
import { ConsultationState } from '../state/consultation-state';

@Injectable({
  providedIn: 'root',
})
export class ConsultationFacade {
  private consultationData = inject(ConsultationDataClient);
  private consultationState = inject(ConsultationState);
  private consentClient = inject(ConsentDataClient);
  private consentState = inject(ConsentState);
  private readonly translocoService = inject(TranslocoService);
  private readonly languageService = inject(LanguageService);
  private userData = inject(UserData);
  private router = inject(Router);

  private userId = computed(() => this.userData.userData().accountId);
  private consultationId = computed(() =>
    this.consultationState.consultationId(),
  );
  private consultationHistory = computed(() =>
    this.consultationState.consultationHistory(),
  );

  /**
   * Parses a value to a number, returning null if the value is empty or invalid
   */
  private parseValueOrNull(value: string | number | undefined): string | null {
    if (value === undefined || value === null || value === '') {
      return null;
    }
    return this.normalizeLeadingDecimal(String(value));
  }

  private normalizeLeadingDecimal(value: string): string {
    const trimmed = value.trim();
    const normalizedSeparator = trimmed.replace(',', '.');

    if (normalizedSeparator.startsWith('-.')) {
      return `-0${normalizedSeparator.slice(1)}`;
    }

    if (normalizedSeparator.startsWith('+.')) {
      return `+0${normalizedSeparator.slice(1)}`;
    }

    if (normalizedSeparator.startsWith('.')) {
      return `0${normalizedSeparator}`;
    }

    return normalizedSeparator;
  }

  public isConsultationFinished = computed<boolean>(() => {
    return this.consultationState.isFinished();
  });

  public consultationResource = resource({
    params: () => ({
      trigger: this.consultationState.trigger(), // Nur bei User-Aktionen
    }),
    loader: async ({ params, previous }) => {
      if (previous.status === 'idle' || params.trigger === 0) {
        return {} as ConsultationResponse;
      }

      const activeLanguage = this.languageService.getActiveDtoLanguageKey();

      const requestPayload: ConsultationRequest = {
        consultation_id: this.consultationId(),
        consultation_history: this.consultationHistory().map((step) => ({
          ...step,
          language: activeLanguage,
        })),
      };

      const response = await lastValueFrom(
        this.consultationData.continueConsultation(
          requestPayload,
          this.userId(),
        ),
      );

      if (!response.success) {
        throw new Error(
          response.errorMessage ??
            this.translocoService.translate('consultation.error-fallback'),
        );
      }

      this.consultationState.updateResponseData({
        recommended_lens: response.data.recommended_lens,
        is_finished: response.data.is_finished,
        is_prescription_valid: response.data.is_prescription_valid,
        consultation_step_title: response.data.consultation_step_title,
        use_cases_recommendations: response.data.use_cases_recommendations,
      });

      if (response.data.recommended_lens) {
        this.loadSecondaryRecommendations();
      }

      if (response.data.questions?.length) {
        const nextStep: ConsultationStep = {
          consultation_step_title: response.data.consultation_step_title,
          content: {
            kind: 'question_type',
            questions: response.data.questions as ConsultationQuestionDto[],
            use_case_recommendations: response.data.use_cases_recommendations,
          },
          language: activeLanguage,
        };

        this.consultationState.appendStep(nextStep);
      }

      if (response.data.consultation_summary) {
        this.consultationState.updateConsultationSummary(
          response.data.consultation_summary,
        );
      }

      return response;
    },
  });

  public async startConsultation() {
    this.consultationState.resetState();
    const consultation = await lastValueFrom(
      this.consultationData.initializeConsultation(),
    );
    this.consultationState.updateConsultationId(
      consultation.data.consultation_id,
    );
  }

  private async loadSecondaryRecommendations(): Promise<void> {
    this.consultationState.setSecondaryRecommendationsLoading(true);
    try {
      const response = await lastValueFrom(
        this.consultationData.getSecondaryRecommendations(
          this.consultationId(),
        ),
      );
      if (response.success) {
        this.consultationState.updateSecondaryRecommendations(
          response.data.Recommendations,
        );
      }
    } finally {
      this.consultationState.setSecondaryRecommendationsLoading(false);
    }
  }

  public async submitConsentAndProceed(): Promise<void> {
    this.consentState.setLoading(true);

    try {
      const response = await lastValueFrom(this.consentClient.submitConsent());

      if (
        response.status === 204 ||
        response.status === 200 ||
        response.body?.isConsentGiven
      ) {
        this.consentState.setHasConsented(true);
        await this.proceedWithNewConsultation();
        this.consentState.setLoading(false);
      } else {
        throw new Error('Consent submission failed');
      }
    } catch (error) {
      this.consentState.setLoading(false);
      throw error;
    }
  }

  private async proceedWithNewConsultation(): Promise<void> {
    await this.router.navigate([ROUTE_PATHS.consultation]);
  }

  public navigateToConsultation(guid: string): void {
    this.router.navigate([ROUTE_PATHS.consultation, guid]);
  }

  private transformQuestionData(
    questions: Question[],
    currentSelectedSuggestions: Suggestion[][],
  ): ConsultationQuestionDto[] {
    return questions.map((question, index) => {
      const selectedSuggestionsForQuestion =
        currentSelectedSuggestions[index] || [];
      const selectedLabels = new Set(
        selectedSuggestionsForQuestion.map((s) => s.label),
      );
      const existingLabels = new Set(
        question.suggestions?.map((s) => s.label) || [],
      );

      const allSuggestions = [
        // Existierende Suggestions mit aktualisiertem value
        ...(question.suggestions?.map((suggestion) => ({
          ...suggestion,
          value: selectedLabels.has(suggestion.label),
        })) || []),
        // Custom Suggestions, die noch nicht existieren
        ...selectedSuggestionsForQuestion
          .filter((selected) => !existingLabels.has(selected.label))
          .map((custom) => ({ ...custom, value: true })),
      ];

      return { ...question, suggestions: allSuggestions };
    });
  }

  public continueConsultation(
    currentQuestionSet: Question[],
    currentSelectedSuggestions: Suggestion[][],
  ) {
    const questions = this.transformQuestionData(
      currentQuestionSet,
      currentSelectedSuggestions,
    );

    const history = this.consultationState.consultationHistory();
    const lastStep = history[history.length - 1];

    if (!lastStep) {
      return;
    }

    const updatedStep: ConsultationStep = {
      ...lastStep,
      content: {
        ...lastStep.content,
        questions,
      },
    };

    this.consultationState.updateLastStepAndTrigger(updatedStep);
  }

  public continueWithPrescriptionData(prescriptionData: PrescriptionData) {
    const activeLanguage = this.languageService.getActiveDtoLanguageKey();

    const step: ConsultationStep = {
      consultation_step_title: null,
      content: {
        kind: 'prescription_data',
        prescription: {
          right: {
            sphere: this.parseValueOrNull(prescriptionData.rightEyeSphere),
            cylinder: this.parseValueOrNull(prescriptionData.rightEyeCylinder),
            axis: this.parseValueOrNull(prescriptionData.rightEyeAxis),
            prism_h: this.parseValueOrNull(prescriptionData.rightPrismH),
            base_h: this.parseValueOrNull(prescriptionData.rightBaseH),
            prism_v: this.parseValueOrNull(prescriptionData.rightPrismV),
            base_v: this.parseValueOrNull(prescriptionData.rightBaseV),
            res_prism: this.parseValueOrNull(prescriptionData.rightResPrism),
            res_base: this.parseValueOrNull(prescriptionData.rightResBase),
          },
          left: {
            sphere: this.parseValueOrNull(prescriptionData.leftEyeSphere),
            cylinder: this.parseValueOrNull(prescriptionData.leftEyeCylinder),
            axis: this.parseValueOrNull(prescriptionData.leftEyeAxis),
            prism_h: this.parseValueOrNull(prescriptionData.leftPrismH),
            base_h: this.parseValueOrNull(prescriptionData.leftBaseH),
            prism_v: this.parseValueOrNull(prescriptionData.leftPrismV),
            base_v: this.parseValueOrNull(prescriptionData.leftBaseV),
            res_prism: this.parseValueOrNull(prescriptionData.leftResPrism),
            res_base: this.parseValueOrNull(prescriptionData.leftResBase),
          },
          addition: this.parseValueOrNull(prescriptionData.rightEyeAddition),
        },
        centration: {
          right: {
            pd: this.parseValueOrNull(prescriptionData.rightPd),
            x: this.parseValueOrNull(prescriptionData.rightX),
            fh: this.parseValueOrNull(prescriptionData.rightFh),
            bvd: this.parseValueOrNull(prescriptionData.rightBvd),
          },
          left: {
            pd: this.parseValueOrNull(prescriptionData.leftPd),
            x: this.parseValueOrNull(prescriptionData.leftX),
            fh: this.parseValueOrNull(prescriptionData.leftFh),
            bvd: this.parseValueOrNull(prescriptionData.leftBvd),
          },
          wrap: this.parseValueOrNull(prescriptionData.wrap),
          pantoscopic_angle: this.parseValueOrNull(
            prescriptionData.pantoscopicAngle,
          ),
          cen_a: this.parseValueOrNull(prescriptionData.centrationA),
          cen_b: this.parseValueOrNull(prescriptionData.centrationB),
          dbl: this.parseValueOrNull(prescriptionData.centrationDBL),
        },
      },
      language: activeLanguage,
    };

    this.consultationState.appendStepAndTrigger(step);
  }

  public async loadOldConsultation(consultationId: string) {
    this.consultationState.resetState();

    const consultation = await lastValueFrom(
      this.consultationData.getOldConsultation(consultationId),
    );

    this.consultationState.updateConsultationId(consultationId);
    this.consultationState.loadConsultationHistory(
      consultation.data.consultation_history,
    );

    this.consultationState.updateConsultationSummary(
      consultation.data.consultation_summary,
    );

    const history = consultation.data.consultation_history;
    const lastStep = history[history.length - 1];

    if (lastStep?.content.kind === 'recommended_lens') {
      this.consultationState.updateResponseData({
        recommended_lens: lastStep.content.recommended_lens ?? null,
        is_finished: consultation.data.is_finished,
        is_prescription_valid: true,
        consultation_step_title: lastStep.consultation_step_title ?? '',
        use_cases_recommendations: [],
      });

      if (lastStep.content.recommended_lens) {
        this.loadSecondaryRecommendations();
      }
    } else if (consultation.data.is_finished) {
      this.consultationState.updateResponseData({
        recommended_lens: null,
        is_finished: true,
        is_prescription_valid: true,
        consultation_step_title: lastStep?.consultation_step_title ?? '',
        use_cases_recommendations: [],
      });
    }
  }

  public async loadFeedbackQuestions() {
    return await lastValueFrom(
      this.consultationData.getFeedbackQuestions(
        this.userData.userData().organisationId,
      ),
    );
  }

  public goBackToStep(stepIndex: number) {
    const history = this.consultationHistory();
    if (stepIndex >= 0 && stepIndex < history.length) {
      this.consultationState.removeStepsAfterIndex(stepIndex);
    }
  }

  public editPrescriptionData(prescriptionData: PrescriptionData) {
    this.consultationState.removeStepsAfterIndex(-1);
    this.continueWithPrescriptionData(prescriptionData);
  }

  public editQuestionAnswer(
    stepIndex: number,
    questions: Question[],
    selectedSuggestions: Suggestion[][],
  ) {
    const answeredQuestions = this.transformQuestionData(
      questions,
      selectedSuggestions,
    );

    this.consultationState.removeStepsAfterIndex(stepIndex);

    const history = this.consultationState.consultationHistory();
    const step = history[stepIndex];

    if (!step) {
      return;
    }

    const updatedStep: ConsultationStep = {
      ...step,
      content: {
        ...step.content,
        questions: answeredQuestions,
      },
    };

    this.consultationState.updateLastStepAndTrigger(updatedStep);
  }
}
